/**
 * CelebrationContext — global "show a celebration" hook used by any
 * action that can unlock a badge (lecture saved, review streak hit, X
 * cards mastered, …). Mount the provider at the App root; call
 * `celebrate(badge)` or `celebrateMany(badges)` from anywhere.
 *
 * Usage:
 *   const { celebrate } = useCelebration();
 *   celebrate({ name: 'First Lecture', icon: '🎙️', description: '...' });
 *
 * Convenience helper `checkAndCelebrate()` POSTs to
 * /api/gamification/badges/check and, if anything was newly earned,
 * fires the modal automatically — so existing code paths just need a
 * single function call after a meaningful action.
 *
 * The modal renders a confetti burst (canvas-confetti, ~1.6KB), a
 * full-screen badge spotlight, and a one-tap "Share" affordance that
 * builds a 1080×1920 PNG client-side via canvas and either invokes the
 * Web Share API or falls back to download.
 */
import { createContext, useCallback, useContext, useState } from 'react';
import axios from 'axios';
import AchievementCelebrationModal from '../components/AchievementCelebrationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CelebrationContext = createContext(null);

export const CelebrationProvider = ({ children }) => {
  // FIFO queue so back-to-back unlocks stack instead of clobbering.
  const [queue, setQueue] = useState([]);

  const celebrate = useCallback((badge) => {
    if (!badge) return;
    setQueue((q) => [...q, badge]);
  }, []);

  const celebrateMany = useCallback((badges) => {
    if (!Array.isArray(badges) || badges.length === 0) return;
    setQueue((q) => [...q, ...badges]);
  }, []);

  const checkAndCelebrate = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/gamification/badges/check`);
      const earned = data?.newly_earned || [];
      if (earned.length > 0) {
        setQueue((q) => [...q, ...earned]);
      }
      return earned;
    } catch (e) {
      // Non-blocking — failures here should never break the caller flow.
      console.warn('checkAndCelebrate failed:', e?.response?.data || e?.message);
      return [];
    }
  }, []);

  const dismiss = useCallback(() => {
    setQueue((q) => q.slice(1));
  }, []);

  const active = queue[0] || null;

  return (
    <CelebrationContext.Provider value={{ celebrate, celebrateMany, checkAndCelebrate }}>
      {children}
      <AchievementCelebrationModal badge={active} onClose={dismiss} />
    </CelebrationContext.Provider>
  );
};

export const useCelebration = () => {
  const ctx = useContext(CelebrationContext);
  if (!ctx) {
    // Soft-fail so callers don't need to wrap in try/catch — useful for
    // pages mounted outside the provider during tests.
    return {
      celebrate: () => {},
      celebrateMany: () => {},
      checkAndCelebrate: async () => [],
    };
  }
  return ctx;
};
